import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AmplitudeEventService, StorageManagerService } from '@core/services';
import { ToastService } from '@shared/services';
import { VerifySignupJWTResponse } from '@shared/models';
import { showSpinner } from '@shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { LoggedOut, RegistrationEmailValidated } from '@integrations/amplitude';

@Injectable({ providedIn: 'root' })
export class AuthService {
  loggedIn = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private http: HttpClient,
    private storeManager: StorageManagerService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private amplitudeEventService: AmplitudeEventService,
  ) {}

  logIn(token: string, destination?: string) {
    this.storeManager.store('token', token);
    this.loggedIn.next(true);
    if (destination) {
      if (destination.startsWith('/onboarding/finish')) {
        this.amplitudeEventService.trackEvent(
          new RegistrationEmailValidated({
            registrationFlowType: 'ppu',
          }),
        );
        this.storeManager.store('userInitialLogin', 'true');
      }
      this.router.navigateByUrl(destination);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  logOut() {
    this.loggedIn.next(false);
    this.amplitudeEventService.trackEvent(new LoggedOut());
    this.http
      .post(`/api/user/v2/auth/logout/`, {})
      .pipe(showSpinner())
      .subscribe({
        next: () => {
          this.storeManager.delete('token');
          this.storeManager.delete('userInitialLogin');
          this.router.navigate(['/login']);
        },
        error: () => {
          this.storeManager.delete('token');
          this.storeManager.delete('userInitialLogin');
          this.router.navigate(['/login']);
        },
      });
  }

  verifyLoginJWT(token: string): Observable<{ key: string }> {
    return this.http.post<{ key: string }>(`/api/user/v2/auth/login/verify/`, {
      token,
    });
  }

  verifySignupJWT(token: string): Observable<VerifySignupJWTResponse> {
    return this.http.post<VerifySignupJWTResponse>(`/api/user/v2/auth/register/verify/`, {
      token,
    });
  }

  showTokenVerifyErrorToast(error: HttpErrorResponse) {
    let message = undefined;
    if (error.status === 401) {
      if (error.error?.error_code === 'invalid_token' || error.error?.error_code === 'token_invalid') {
        message = 'Common.Errors.TokenLinkInvalid';
      } else if (error.error?.error_code === 'token_expired') {
        message = 'Common.Errors.TokenLinkExpired';
      } else if (error.error?.error_code === 'token_not_found') {
        message = 'Common.Errors.TokenLinkAlreadyUsed';
      }
    }
    if (error.status === 400) {
      if (error.error?.error_code === 'license_plate_used') {
        message = 'Common.Errors.TokenLicensePLateAlreadyUsed';
      }
      if (error.error?.error_code === 'email_used') {
        message = 'Common.Errors.TokenEmailAlreadyUsed';
      }
    }
    this.toastService.add(
      {
        message: this.translateService.instant(message ?? 'Common.Errors.UnknownError'),
        type: 'error',
      },
      10000,
    );
  }
}
